import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Milestone } from "../../models/Milestones.model";
import {
  createOrUpdeteMilestone,
  deleteMilestone,
  getAllMilestones,
  getAllBaseMilestones,
  updateMilestone,
  updateMilestonePositionIdx,
} from "../thunks/milestonesThunks";
import { getLeadData } from "../thunks/leadThunks";

interface MilestonesState {
  // נתוני אבני דרך
  milestones: Milestone[];
  loading: boolean;
  error: string | null;

  // פעולות עדכון
  updateLoading: boolean;
  updateError: string | null;
  updateSuccess: boolean;
  updateMessage: string;

  // פרויקט נוכחי
  currentProjectId: number | null;

  // כללי
  initialized: boolean;
}

const initialState: MilestonesState = {
  milestones: [],
  loading: false,
  error: null,
  updateLoading: false,
  updateError: null,
  updateSuccess: false,
  updateMessage: "",
  currentProjectId: null,
  initialized: false,
};

const milestonesSlice = createSlice({
  name: "milestones",
  initialState,
  reducers: {
    clearMessages: (state) => {
      state.error = null;
      state.updateError = null;
      state.updateMessage = "";
      state.updateSuccess = false;
    },
    resetMilestonesState: (state) => {
      Object.assign(state, initialState);
    },
    setCurrentProject: (state, action: PayloadAction<number>) => {
      state.currentProjectId = action.payload;
      state.milestones = [];
      state.error = null;
      state.initialized = false;
    },
    clearMilestonesOnProjectChange: (state) => {
      state.milestones = [];
      state.currentProjectId = null;
      state.initialized = false;
      state.error = null;
    },
    resetInitialized: (state) => {
      state.initialized = false;
    },
    clearSuccess: (state) => {
      state.updateSuccess = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get All Milestones
      .addCase(getAllMilestones.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllMilestones.fulfilled, (state, action) => {
        state.loading = false;
        state.milestones = action.payload.milestones || [];
        state.initialized = true;
      })
      .addCase(getAllMilestones.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) || "Failed to fetch milestones";
      })
      .addCase(getLeadData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getLeadData.fulfilled, (state, action) => {
        state.loading = false;
        state.milestones = action.payload.lead.milestones.map((m) => ({
          milestoneLastUpdate: m.projectMilestone_LastUpdate,
          milestoneID: m.milestone_ID,
          milestoneComment: m.projectMilestone_Comment,
          milestoneDescription: m.milestone_Description,
          milestoneLabel: m.milestone_Label,
          status: m.status ? 1 : 0,
          webUserFullName: m.webUser_FullName || "",
          //            milestoneID: number;
          // milestoneLabel: string;
          // milestoneDescription: string;
          // status: number;
          // milestoneComment: string;
          // milestoneLastUpdate: string;
          // webUserFullName: string;
        }));
        state.initialized = true;
      })
      .addCase(getLeadData.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || "Failed to fetch lead data";
      })

      // Get All Base Milestones (עבור הגדרות)
      .addCase(getAllBaseMilestones.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllBaseMilestones.fulfilled, (state, action) => {
        state.loading = false;
        state.milestones = action.payload.milestones || [];
        state.initialized = true;
      })
      .addCase(getAllBaseMilestones.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) || "Failed to fetch base milestones";
      })

      .addCase(createOrUpdeteMilestone.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.updateSuccess = false;
      })
      .addCase(createOrUpdeteMilestone.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.updateSuccess = true;

        const milestoneId = action.payload.milestoneID;
        const milestone = action.meta.arg;

        if (milestone.milestoneID === 0) {
          const newMilestone = {
            milestoneID: milestoneId,
            milestoneLabel: milestone.milestoneLabel,
            milestoneDescription: milestone.milestoneDescription,
            status: 0,
            milestoneComment: "",
            milestoneLastUpdate: new Date().toISOString(),
            webUserFullName: "",
          };
          state.milestones.push(newMilestone);
        } else {
          const milestoneIndex = state.milestones.findIndex(
            (m) => m.milestoneID === milestone.milestoneID,
          );

          if (milestoneIndex !== -1) {
            state.milestones[milestoneIndex] = {
              ...state.milestones[milestoneIndex],
              milestoneLabel: milestone.milestoneLabel,
              milestoneDescription: milestone.milestoneDescription,
              milestoneLastUpdate: new Date().toISOString(),
            };
          }
        }
      })
      .addCase(createOrUpdeteMilestone.rejected, (state, action) => {
        state.loading = false;
        state.updateSuccess = false;
        state.error =
          (action.payload as string) || "Failed to create or update milestone";
      })
      .addCase(deleteMilestone.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteMilestone.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        const milestoneId = action.meta.arg.milestoneID;
        state.milestones = state.milestones.filter(
          (milestone) => milestone.milestoneID !== milestoneId,
        );
      })
      .addCase(deleteMilestone.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) || "Failed to delete milestone";
      })
      // Update Milestone
      .addCase(updateMilestone.pending, (state) => {
        state.updateLoading = true;
        state.updateError = null;
        state.updateSuccess = false;
      })
      .addCase(updateMilestone.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.updateSuccess = true;
        state.updateMessage = action.payload.message;

        // עדכן את הסטטוס במילסטון המקומי
        const requestData = action.meta.arg;
        const milestoneIndex = state.milestones.findIndex(
          (m) => m.milestoneID === requestData.milestoneID,
        );

        if (milestoneIndex !== -1) {
          state.milestones[milestoneIndex] = {
            ...state.milestones[milestoneIndex],
            status: requestData.status,
            milestoneLastUpdate: new Date().toISOString(), // שמור כstring
            webUserFullName: (requestData as any).userFullName || "",
          };
        }
      })
      .addCase(updateMilestone.rejected, (state, action) => {
        state.updateLoading = false;
        state.updateSuccess = false;
        state.updateError =
          (action.payload as string) || "Failed to update milestone";
      })
      // הוסף בסוף ה-extraReducers, לפני הסגירה
      .addCase(updateMilestonePositionIdx.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateMilestonePositionIdx.fulfilled, (state, _action) => {
        state.loading = false;
        state.error = null;

        // ✅ רענון הנתונים - נקה את המטמון כדי לגרום לטעינה מחדש
        state.initialized = false;
      })
      .addCase(updateMilestonePositionIdx.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) || "Failed to update milestone position";
      });
  },
});

export const {
  clearMessages,
  resetMilestonesState,
  setCurrentProject,
  resetInitialized,
  clearMilestonesOnProjectChange,
} = milestonesSlice.actions;

export default milestonesSlice.reducer;

// Selectors
export const selectMilestones = (state: { milestones: MilestonesState }) =>
  state.milestones?.milestones || [];

export const selectMilestonesLoading = (state: {
  milestones: MilestonesState;
}) => state.milestones?.loading || false;

export const selectMilestonesError = (state: { milestones: MilestonesState }) =>
  state.milestones?.error || null;

export const selectMilestonesInitialized = (state: {
  milestones: MilestonesState;
}) => state.milestones?.initialized || false;
export const selectUpdateLoading = (state: { milestones: MilestonesState }) =>
  state.milestones?.updateLoading || false;

export const selectUpdateError = (state: { milestones: MilestonesState }) =>
  state.milestones?.updateError || null;

export const selectUpdateSuccess = (state: { milestones: MilestonesState }) =>
  state.milestones?.updateSuccess || false;

export const selectUpdateMessage = (state: { milestones: MilestonesState }) =>
  state.milestones?.updateMessage || "";

export const selectCurrentProjectId = (state: {
  milestones: MilestonesState;
}) => state.milestones?.currentProjectId || null;

// Computed selectors
export const selectMilestoneById =
  (milestoneId: number) => (state: { milestones: MilestonesState }) =>
    state.milestones?.milestones?.find(
      (milestone) => milestone.milestoneID === milestoneId,
    );

export const selectMilestonesByStatus =
  (status: number) => (state: { milestones: MilestonesState }) =>
    state.milestones?.milestones?.filter(
      (milestone) => milestone.status === status,
    ) || [];

export const selectCompletedMilestones = (state: {
  milestones: MilestonesState;
}) =>
  state.milestones?.milestones?.filter((milestone) => milestone.status === 1) ||
  [];

export const selectPendingMilestones = (state: {
  milestones: MilestonesState;
}) =>
  state.milestones?.milestones?.filter((milestone) => milestone.status === 0) ||
  [];

export const selectMilestonesCount = (state: { milestones: MilestonesState }) =>
  state.milestones?.milestones?.length || 0;

export const selectCompletedMilestonesCount = (state: {
  milestones: MilestonesState;
}) =>
  state.milestones?.milestones?.filter((milestone) => milestone.status === 1)
    ?.length || 0;
